import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon, CameraIcon } from "@heroicons/react/24/outline";
import Spinner from "../../../ui/Spinner";

const VP = 300;
const CROP_OUTPUT_SIZE = 256;
const MAX_ZOOM = 5;
const ELASTIC_RESISTANCE = 0.35;
const SNAP_BACK_MS = 300;

function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

export default function AvatarCropModal({ imageUrl, onSave, onClose }) {
  const [zoomFactor, setZoomFactor] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);
  const [isSnapping, setIsSnapping] = useState(false);

  const imgRef = useRef(null);
  const viewportRef = useRef(null);
  const containScaleRef = useRef(1);
  const cropRadiusRef = useRef(VP / 2);
  const handleWheelRef = useRef(null);

  const dragStartRef = useRef(null);
  const pinchRef = useRef(null);
  const activePointersRef = useRef(new Map());
  const containWRef = useRef(0);
  const containHRef = useRef(0);
  const minZoomRef = useRef(1);

  // Hard-limit clamp: returns the nearest legal offset for a given zoom factor.
  // The image must fully cover the VP x VP viewport (and thus the inscribed circle).
  function clampOff(off, zf) {
    const maxX = Math.max(0, (containWRef.current * zf - VP) / 2);
    const maxY = Math.max(0, (containHRef.current * zf - VP) / 2);
    return {
      x: clamp(off.x, -maxX, maxX),
      y: clamp(off.y, -maxY, maxY),
    };
  }

  // Elastic clamp: used during active drag/pinch. Allows overscroll past hard
  // limits with diminishing resistance, so the image can visually go a little
  // past the edge before snapping back on pointer-up.
  function elasticClamp(off, zf) {
    const maxX = Math.max(0, (containWRef.current * zf - VP) / 2);
    const maxY = Math.max(0, (containHRef.current * zf - VP) / 2);
    return {
      x: elasticAxis(off.x, maxX),
      y: elasticAxis(off.y, maxY),
    };
  }

  // Per-axis elastic: if |val| <= limit, pass through; otherwise apply resistance.
  function elasticAxis(val, limit) {
    if (val > limit) return limit + (val - limit) * ELASTIC_RESISTANCE;
    if (val < -limit) return -limit + (val + limit) * ELASTIC_RESISTANCE;
    return val;
  }

  // Elastic zoom: if zf < minZoom, resist the underscroll.
  // Returns a "display zoom" that allows visual feedback without breaking coverage.
  function elasticZoom(zf) {
    const minZf = minZoomRef.current;
    if (zf >= minZf) return zf;
    // Resistance on the zoom ratio: how far below minZoom the user has pulled.
    const underscroll = minZf - zf;
    return minZf - underscroll * ELASTIC_RESISTANCE;
  }

  function zoomToward(px, py, newZf, curZf, curOff) {
    const ratio = newZf / curZf;
    return clampOff(
      {
        x: (px - VP / 2) * (1 - ratio) + curOff.x * ratio,
        y: (py - VP / 2) * (1 - ratio) + curOff.y * ratio,
      },
      newZf,
    );
  }

  const handleImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;

    const nW = img.naturalWidth;
    const nH = img.naturalHeight;
    containScaleRef.current = Math.min(VP / nW, VP / nH);

    containWRef.current = nW * containScaleRef.current;
    containHRef.current = nH * containScaleRef.current;

    // Crop circle is always VP/2 -- the full inscribed circle of the viewport.
    cropRadiusRef.current = VP / 2;

    // Minimum zoom so that the image covers the viewport in both axes.
    minZoomRef.current = VP / Math.min(containWRef.current, containHRef.current);

    setZoomFactor(minZoomRef.current);   // start at minimum legal zoom, not 1
    setOffset({ x: 0, y: 0 });
    setImgLoaded(true);
  };

  const handlePointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    activePointersRef.current.set(e.pointerId, {
      clientX: e.clientX,
      clientY: e.clientY,
    });

    if (activePointersRef.current.size === 2) {
      if (isSnapping) setIsSnapping(false);
      const points = [...activePointersRef.current.values()];
      const rect = viewportRef.current.getBoundingClientRect();
      pinchRef.current = {
        initialDist: Math.hypot(
          points[1].clientX - points[0].clientX,
          points[1].clientY - points[0].clientY,
        ),
        initialZf: zoomFactor,
        initialOffset: { ...offset },
        midX: (points[0].clientX + points[1].clientX) / 2 - rect.left,
        midY: (points[0].clientY + points[1].clientY) / 2 - rect.top,
      };
      dragStartRef.current = null;
    } else {
      if (isSnapping) setIsSnapping(false);
      dragStartRef.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        offsetX: offset.x,
        offsetY: offset.y,
      };
    }
  };

  const handlePointerMove = (e) => {
    activePointersRef.current.set(e.pointerId, {
      clientX: e.clientX,
      clientY: e.clientY,
    });

    if (pinchRef.current && activePointersRef.current.size === 2) {
      const points = [...activePointersRef.current.values()];
      const currentDist = Math.hypot(
        points[1].clientX - points[0].clientX,
        points[1].clientY - points[0].clientY,
      );
      const { initialDist, initialZf, initialOffset, midX, midY } =
        pinchRef.current;

      // Raw zoom intent -- allow below minZoom for elastic feel, but floor at
      // a fraction of minZoom so it doesn't go crazy.
      const rawZf = initialZf * (currentDist / initialDist);
      const floorZf = minZoomRef.current * 0.7;
      const boundedZf = clamp(rawZf, floorZf, MAX_ZOOM);

      // For layout/offset math use the "display" zoom (elastic-squished if below min).
      const displayZf = boundedZf < minZoomRef.current ? elasticZoom(boundedZf) : boundedZf;

      // Store the raw (possibly sub-minZoom) value in state so snap-back knows
      // whether we need to correct. But use displayZf for the visual transform.
      setZoomFactor(displayZf);

      // Compute offset toward pinch midpoint, then elastic-clamp it.
      const ratio = displayZf / initialZf;
      const rawOff = {
        x: (midX - VP / 2) * (1 - ratio) + initialOffset.x * ratio,
        y: (midY - VP / 2) * (1 - ratio) + initialOffset.y * ratio,
      };
      setOffset(elasticClamp(rawOff, displayZf));

      // Store raw zoom so pointer-up can snap back if needed.
      pinchRef.current.rawZf = rawZf;
    } else if (dragStartRef.current) {
      const dx = e.clientX - dragStartRef.current.clientX;
      const dy = e.clientY - dragStartRef.current.clientY;
      // Disable any active snap transition during drag.
      if (isSnapping) setIsSnapping(false);
      setOffset(
        elasticClamp(
          {
            x: dragStartRef.current.offsetX + dx,
            y: dragStartRef.current.offsetY + dy,
          },
          zoomFactor,
        ),
      );
    }
  };

  const handlePointerUp = (e) => {
    activePointersRef.current.delete(e.pointerId);
    if (activePointersRef.current.size < 2) {
      // Pinch just ended. Check if zoom needs to snap back to minZoom.
      if (pinchRef.current && pinchRef.current.rawZf != null) {
        const minZf = minZoomRef.current;
        if (pinchRef.current.rawZf < minZf) {
          // Snap zoom back to minZoom and clamp offset to legal range.
          setIsSnapping(true);
          setZoomFactor(minZf);
          setOffset((prev) => clampOff(prev, minZf));
        }
      }
      pinchRef.current = null;
    }
    if (activePointersRef.current.size === 0) {
      // Single-finger drag just ended. Snap offset back to hard limits.
      setIsSnapping(true);
      setOffset((prev) => clampOff(prev, zoomFactor));
      dragStartRef.current = null;
    }
  };

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      if (handleWheelRef.current) handleWheelRef.current(e);
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  useEffect(() => {
    handleWheelRef.current = (e) => {
      if (!imgLoaded) return;

      const direction = e.deltaY > 0 ? -1 : 1;
      const newZf = clamp(
        zoomFactor * (1 + direction * 0.1),
        minZoomRef.current,
        MAX_ZOOM,
      );

      const rect = viewportRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      setZoomFactor(newZf);
      setOffset(zoomToward(mouseX, mouseY, newZf, zoomFactor, offset));
    };
  });

  const handleSave = async () => {
    const img = imgRef.current;
    if (!img) return;

    const nW = img.naturalWidth;
    const nH = img.naturalHeight;
    const cropRadius = cropRadiusRef.current;
    const cropDiam = cropRadius * 2;
    const scale = containScaleRef.current * zoomFactor;

    const sx = nW / 2 - (cropRadius + offset.x) / scale;
    const sy = nH / 2 - (cropRadius + offset.y) / scale;
    const sw = cropDiam / scale;
    const sh = cropDiam / scale;

    const canvas = document.createElement("canvas");
    canvas.width = CROP_OUTPUT_SIZE;
    canvas.height = CROP_OUTPUT_SIZE;
    const ctx = canvas.getContext("2d");

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, CROP_OUTPUT_SIZE, CROP_OUTPUT_SIZE);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9);
    });

    onSave(blob);
  };

  const cropRadius = VP / 2;  // always fills the viewport

  const modal = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md rounded-2xl bg-surface-elevated border border-border shadow-2xl overflow-hidden">
        <header className="flex items-center justify-between px-5 py-3 border-b border-border bg-surface">
          <div className="flex items-center gap-2 text-text-primary">
            <CameraIcon className="h-5 w-5 text-primary" />
            <h3 className="text-sm font-semibold">Adjust avatar</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-surface-sunken"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5 text-text-secondary" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <p className="text-[13px] text-text-secondary text-center">
            Drag to pan · Scroll or pinch to zoom
          </p>

          <div className="flex justify-center">
            <div
              ref={viewportRef}
              className="relative overflow-hidden rounded-lg bg-black"
              style={{ width: VP, height: VP, touchAction: "none" }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              <img
                ref={imgRef}
                src={imageUrl}
                alt="Crop preview"
                draggable={false}
                onLoad={handleImgLoad}
                className="absolute pointer-events-none select-none"
                style={
                  imgLoaded
                    ? {
                        width: containWRef.current,
                        height: containHRef.current,
                        left: (VP - containWRef.current) / 2,
                        top: (VP - containHRef.current) / 2,
                        transform: `translate3d(${offset.x}px, ${offset.y}px, 0px) scale(${zoomFactor})`,
                        transformOrigin: "center center",
                        willChange: "transform",
                        transition: isSnapping
                          ? `transform ${SNAP_BACK_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
                          : "none",
                      }
                    : { visibility: "hidden" }
                }
                onTransitionEnd={() => setIsSnapping(false)}
              />

              {imgLoaded && (
                <svg
                  className="absolute inset-0 pointer-events-none"
                  width={VP}
                  height={VP}
                  shapeRendering="geometricPrecision"
                >
                  <defs>
                    <mask id="avatarCropMask">
                      <rect width={VP} height={VP} fill="white" />
                      <circle
                        cx={VP / 2}
                        cy={VP / 2}
                        r={cropRadius}
                        fill="black"
                      />
                    </mask>
                  </defs>
                  <rect
                    width={VP}
                    height={VP}
                    fill="rgba(0,0,0,0.55)"
                    mask="url(#avatarCropMask)"
                  />
                  <circle
                    cx={VP / 2}
                    cy={VP / 2}
                    r={cropRadius}
                    fill="none"
                    stroke="rgba(255,255,255,0.7)"
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}

              {!imgLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Spinner size={28} className="text-white" />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <button
              className="px-3 py-1.5 rounded-md bg-surface-sunken text-text-secondary border border-border-strong text-sm hover:bg-surface"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-4 py-1.5 rounded-md bg-primary hover:bg-primary-hover text-text-inverse text-sm disabled:opacity-60"
              onClick={handleSave}
              disabled={!imgLoaded}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
